/* eslint-disable no-prototype-builtins */
import { IHttpClient } from 'aurelia';
import { IConfigService } from './config-service';
import { ITokenService } from './token-service';
import { ResponseResults } from '../resources/models/response-results';
import { REQUEST_TYPE_POST, ErrorMessages } from '../resources/constants';
import { DI } from '@aurelia/kernel';

export const IRegisterLoginService = DI.createInterface<IRegisterLoginService>(
    'IRegisterLoginService',
    (x) => x.singleton(RegisterLoginService)
);
export interface IRegisterLoginService extends RegisterLoginService {}

//@inject(HttpClient, ConfigService, ErrorMessages, TokenService)
export class RegisterLoginService {
    private clientApiHost;
    private appClient;
    private errorMessages = ErrorMessages;

    constructor(
        @IHttpClient readonly http: IHttpClient,
        @IConfigService readonly configService: IConfigService,
        @ITokenService readonly tokenService: ITokenService
    ) {
        this.clientApiHost = this.configService.get('clientApiHost');
        this.appClient = this.configService.get('appClient');
    }

    async registerEmail(schemeGuid, email, password) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/schemes/${schemeGuid}/account/register`;
            const registerResult = await this.http.fetch(url, {
                method: REQUEST_TYPE_POST,
                body: JSON.stringify({
                    Email: email,
                    Username: email,
                    Password: password,
                    ConfirmPassword: password,
                }),
            });

            responseModel.setStatus(registerResult.status);
            responseModel.setResults(await registerResult.json());
        } catch (error) {
            responseModel.setStatus(error.status);

            if (responseModel.getStatus() === 400) {
                const serverError = await error.json();

                if (serverError.hasOwnProperty('passwordTooShort')) {
                    responseModel.setErrors({
                        code: 'passwordTooShort',
                        message: this.errorMessages.PasswordTooShort,
                    });
                }

                if (serverError.hasOwnProperty('passwordRequiresDigit')) {
                    responseModel.setErrors({
                        code: 'passwordRequiresDigit',
                        message: this.errorMessages.PasswordRequiresDigit,
                    });
                }

                if (serverError.hasOwnProperty('passwordRequiresLower')) {
                    responseModel.setErrors({
                        code: 'passwordRequiresLower',
                        message: this.errorMessages.PasswordRequiresLower,
                    });
                }

                if (
                    serverError.hasOwnProperty(
                        'passwordRequiresNonAlphanumeric'
                    )
                ) {
                    responseModel.setErrors({
                        code: 'passwordRequiresNonAlphanumeric',
                        message:
                            this.errorMessages.PasswordRequiresNonAlphanumeric,
                    });
                }

                if (serverError.hasOwnProperty('passwordRequiresUpper')) {
                    responseModel.setErrors({
                        code: 'passwordRequiresUpper',
                        message: this.errorMessages.PasswordRequiresUpper,
                    });
                }

                if (serverError.hasOwnProperty('DuplicateUserName')) {
                    responseModel.setErrors({
                        code: 'DuplicateUserName',
                        message: this.errorMessages.EmailAlreadyRegistered,
                    });
                }

                if (serverError.hasOwnProperty('clientCode')) {
                    responseModel.setErrors({
                        code: 'clientCode',
                        message: this.errorMessages.EmailAlreadyRegistered,
                    });
                }

                if (serverError.hasOwnProperty('userInAdviserDb')) {
                    responseModel.setErrors({
                        code: 'userInAdviserDb',
                        message: this.errorMessages.EmailAlreadyRegistered,
                    });
                }
            }

            if (responseModel.getStatus() === 500) {
                responseModel.setErrors({
                    code: 'UnspecifiedError',
                    message: this.errorMessages.UnspecifiedError,
                });
            }
        }

        return responseModel;
    }

    async activateEmail(userId, verificationCode) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/api/account/confirmemail?userId=${userId}&code=${verificationCode}`;
            const activateResult = await this.http.fetch(url);
            responseModel.setStatus(activateResult.status);
        } catch (error) {
            responseModel.setStatus(error.status);

            if (responseModel.getStatus() === 400) {
                const serverError = await error.json();

                if (serverError.hasOwnProperty('emailTokenNotFound')) {
                    responseModel.setErrors({
                        code: 'emailTokenNotFound',
                        message: this.errorMessages.InvalidActivationCode,
                    });
                }
            }

            if (responseModel.getStatus() === 500) {
                responseModel.setErrors({
                    code: 'UnspecifiedError',
                    message: this.errorMessages.UnspecifiedError,
                });
            }
        }

        return responseModel;
    }

    async sendActivationCode(email) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/api/account/sendemailactivation?email=${email}`;
            const emailResult = await this.http.fetch(url);
            responseModel.setStatus(emailResult.status);
        } catch (error) {
            responseModel.setStatus(500);
            responseModel.setErrors({
                code: 'UnspecifiedError',
                message: this.errorMessages.UnspecifiedError,
            });
        }

        return responseModel;
    }

    async resetPassword(email) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/api/account/resetpassword/generatelink?email=${email}`;
            const resetResult = await this.http.fetch(url, {
                method: REQUEST_TYPE_POST,
            });
            responseModel.setStatus(resetResult.status);
        } catch (error) {
            responseModel.setStatus(500);
            responseModel.setErrors({
                code: 'UnspecifiedError',
                message: this.errorMessages.UnspecifiedError,
            });
        }

        return responseModel;
    }

    async login(username, password) {
        return await this.tokenService.createAuthToken(username, password);
    }

    async postContactDetails(entityType, body) {
        const responseModel = new ResponseResults();

        try {
            const url = `${this.clientApiHost}/party/${entityType}`;
            const postResult = await this.http.fetch(url, {
                method: REQUEST_TYPE_POST,
                body: JSON.stringify(body),
            });
            responseModel.setStatus(postResult.status);
        } catch (error) {
            responseModel.setStatus(500);
            responseModel.setErrors({
                code: 'UnspecifiedError',
                message: this.errorMessages.UnspecifiedError,
            });
        }

        return responseModel;
    }
}
